import { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { loginWithGoogle, loginWithEmail, registerWithEmail, getIdToken } from '../../config/firebase';

interface LoginProps {
  onLoginSuccess: (user: any, token: string) => void;
}

export function Login({ onLoginSuccess }: LoginProps) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsApproval, setNeedsApproval] = useState(false);

  const handleSuccess = async (user: any, isAdmin: boolean) => {
    const token = await getIdToken();
    if (!token) {
      throw new Error('No se pudo obtener el token');
    }

    sessionStorage.setItem('auth_token', token);
    sessionStorage.setItem('user', JSON.stringify({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || name,
      isAdmin
    }));

    onLoginSuccess({
      uid: user.uid,
      email: user.email,
      username: user.displayName || name || user.email?.split('@')[0],
      isAdmin
    }, token);
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);

    try {
      const { user, isAdmin } = await loginWithGoogle();
      await handleSuccess(user, isAdmin);
    } catch (err: any) {
      if (err.message === 'NEEDS_APPROVAL') {
        setNeedsApproval(true);
      } else if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message || 'Error al iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { user, isAdmin } = await loginWithEmail(email, password);
      await handleSuccess(user, isAdmin);
    } catch (err: any) {
      if (err.message === 'NEEDS_APPROVAL') {
        setNeedsApproval(true);
      } else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Email o contraseña incorrectos');
      } else if (err.code === 'auth/invalid-email') {
        setError('El formato del email no es válido');
      } else {
        setError(err.message || 'Error al iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await registerWithEmail(email, password, name);
      setNeedsApproval(true);
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        setError('Este email ya está registrado. Intenta iniciar sesión.');
      } else if (err.code === 'auth/weak-password') {
        setError('La contraseña es muy débil');
      } else {
        setError(err.message || 'Error al registrarse');
      }
    } finally {
      setLoading(false);
    }
  };

  if (needsApproval) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Solicitud enviada</h2>
          <p className="text-gray-600 mb-6">
            Tu solicitud de acceso ha sido enviada al administrador.
            Recibirás acceso cuando sea aprobada.
          </p>
          <Button
            onClick={() => {
              setNeedsApproval(false);
              setMode('login');
              setEmail('');
              setPassword('');
              setName('');
            }}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700"
          >
            Volver al login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 w-full">
      {/* Logo y título */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full mb-3">
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          Generador de Contenido
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {mode === 'login' ? 'Inicia sesión para continuar' : 'Solicita acceso'}
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert type="error">{error}</Alert>
        </div>
      )}

      {/* Botón de Google */}
      <button
        type="button"
        onClick={handleGoogleLogin}
        disabled={loading}
        className="w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm flex items-center justify-center gap-2 transition-all mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span>Continuar con Google</span>
      </button>

      {/* Separador */}
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-400">o</span>
        </div>
      </div>

      {/* Formulario Email/Password */}
      <form onSubmit={mode === 'login' ? handleEmailLogin : handleRegister} className="space-y-3">
        {mode === 'register' && (
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre completo"
            required
            disabled={loading}
            className="w-full"
          />
        )}

        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          required
          disabled={loading}
          className="w-full"
        />

        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Contraseña"
          required
          disabled={loading}
          className="w-full"
        />

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium py-2.5 rounded-lg"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              {mode === 'login' ? 'Iniciando sesión...' : 'Registrando...'}
            </span>
          ) : (
            mode === 'login' ? 'Iniciar sesión' : 'Solicitar acceso'
          )}
        </Button>
      </form>

      {/* Toggle login/register */}
      <div className="mt-4 text-center">
        <button
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            setError(null);
          }}
          className="text-purple-600 hover:text-purple-700 text-sm font-medium"
        >
          {mode === 'login' ? '¿No tienes cuenta? Solicita acceso' : '¿Ya tienes cuenta? Inicia sesión'}
        </button>
      </div>

      <div className="mt-4 text-center text-xs text-gray-400">
        Solo usuarios autorizados pueden acceder
      </div>
    </div>
  );
}
